import { useState } from "react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { useNavigate, useLocation } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { api, ApiClientError } from "@/lib/api-client"
import { useAuthStore } from "@/stores/auth-store"
import { Button } from "@/components/ui/button"

const mfaSchema = z.object({
  code: z.string().min(6).max(6),
})

const backupSchema = z.object({
  backup_code: z.string().min(1),
})

type MfaForm = z.infer<typeof mfaSchema>
type BackupForm = z.infer<typeof backupSchema>

export function MfaVerifyPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { setUser } = useAuthStore()
  const [useBackup, setUseBackup] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const userId = (location.state as { userId?: string })?.userId

  const mfaForm = useForm<MfaForm>({
    resolver: zodResolver(mfaSchema),
  })

  const backupForm = useForm<BackupForm>({
    resolver: zodResolver(backupSchema),
  })

  const onSubmitCode = async (data: MfaForm) => {
    try {
      setError(null)
      const response = await api.post<{ user: Parameters<typeof setUser>[0] }>(
        "/api/v1/auth/mfa/verify",
        { user_id: userId, code: data.code }
      )
      setUser(response.user)
      navigate("/dashboard")
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError("Wystąpił nieoczekiwany błąd")
      }
    }
  }

  const onSubmitBackup = async (data: BackupForm) => {
    try {
      setError(null)
      const response = await api.post<{ user: Parameters<typeof setUser>[0] }>(
        "/api/v1/auth/mfa/verify",
        { user_id: userId, backup_code: data.backup_code }
      )
      setUser(response.user)
      navigate("/dashboard")
    } catch (err) {
      if (err instanceof ApiClientError) {
        setError(err.message)
      } else {
        setError("Wystąpił nieoczekiwany błąd")
      }
    }
  }

  return (
    <div className="space-y-4">
      <h2 className="page-title">
        {t("auth.mfa.title")}
      </h2>

      {error && (
        <div
          className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300"
        >
          {error}
        </div>
      )}

      {useBackup ? (
        <form onSubmit={backupForm.handleSubmit(onSubmitBackup)} className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">
              {t("auth.mfa.backupCode")}
            </label>
            <input
              type="text"
              autoComplete="off"
              {...backupForm.register("backup_code")}
              className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm text-foreground outline-none font-mono ${backupForm.formState.errors.backup_code ? "border-red-500" : "border-input"}`}
            />
          </div>

          <Button type="submit" disabled={backupForm.formState.isSubmitting} className="w-full">
            {backupForm.formState.isSubmitting ? t("common.loading") : t("auth.mfa.verify")}
          </Button>

          <Button
            type="button"
            variant="link"
            onClick={() => setUseBackup(false)}
            className="w-full"
          >
            {t("auth.mfa.useCode")}
          </Button>
        </form>
      ) : (
        <form onSubmit={mfaForm.handleSubmit(onSubmitCode)} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {t("auth.mfa.enterCode")}
          </p>

          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">
              {t("auth.mfa.code")}
            </label>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              {...mfaForm.register("code")}
              className={`h-9 w-full rounded-md border bg-transparent px-3 text-sm text-foreground outline-none font-mono tracking-widest text-center ${mfaForm.formState.errors.code ? "border-red-500" : "border-input"}`}
            />
          </div>

          <Button type="submit" disabled={mfaForm.formState.isSubmitting} className="w-full">
            {mfaForm.formState.isSubmitting ? t("common.loading") : t("auth.mfa.verify")}
          </Button>

          <Button
            type="button"
            variant="link"
            onClick={() => setUseBackup(true)}
            className="w-full"
          >
            {t("auth.mfa.useBackupCode")}
          </Button>
        </form>
      )}
    </div>
  )
}
